import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DataFeedService } from '../../../shared/api/datafeed.service';
import { AppService } from '../../../store/app.service';
import { IClient } from '../../../core/models/clients.models';
import { ICompany } from '../../../core/models/datafeed.models';
import { CampaignsApiService } from '../../campaigns/campaigns.apiservice';
import { EmailRequestModel, FormsApiService } from '../forms.apiservice';
import { UsersApiService } from '../../users/users.apiservice';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { forkJoin, of, ReplaySubject } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { createGuid } from '../../../core/helpers/utility';
import { TasksApiService } from '../../tasks/tasks.apiservice';
import { INotes } from '../../../core/models/campaigns.model';
import { ITask } from '../../../core/models/tasks.models';
import { Utility } from '../../../shared/utility';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-creative-request',
  templateUrl: './creative-request.component.html',
  styleUrls: ['./creative-request.component.scss']
})
export class CreativeRequestComponent implements OnInit, OnDestroy, OnChanges {
  @Input() campaignId?: string;
  @Input() clientId?: string;
  creativeForm: FormGroup;
  allClients: IClient[] = [];
  campaignsData: Array<any> = [];
  allCampaigns: Array<any> = [];
  creativeUsers: Array<any> = [];
  companiesData: Array<ICompany> = [];

  private readonly copyTypeOtherValue = 'Other';
  private readonly weekdayDateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const date = this.parseDateOnlyToLocalDate(control.value);
    return date && (date.getDay() === 0 || date.getDay() === 6) ? { weekend: true } : null;
  };

  private lastAutoBrandName: string | null = null;
  private lastAutoBrandClientId: string | null = null;

  private requestorEmail: string = '';

  private readonly copyCreativeValue = 'Copy & Creative';

  public requestTypeOptions: Array<{ value: string; label: string }> = [];

  public projectTypeOptions: Array<{ value: string; label: string }> = [];

  private readonly allowedAssigneeNames: Array<{ firstName: string; lastName: string }> = [
    { firstName: 'Lynette', lastName: 'Castañon' },
    { firstName: 'Giselle', lastName: 'Ramirez' },
    { firstName: 'Kirsten', lastName: 'Maglunog' },
  ];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  private normalizeId(value: unknown): string {
    return String(value ?? '').trim();
  }

  private getCampaignClientId(campaign: any): string {
    if (!campaign) {
      return '';
    }

    // API payloads in this app aren't always consistent in casing.
    return this.normalizeId(
      campaign.clientId ?? campaign.clientID ?? campaign.client_id ?? campaign.ClientId ?? campaign.ClientID
    );
  }

  private normalizeNamePart(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private makeNameKey(firstName: unknown, lastName: unknown): string {
    return `${this.normalizeNamePart(firstName)}|${this.normalizeNamePart(lastName)}`;
  }

  constructor(
    private fb: FormBuilder,
    private dataFeedService: DataFeedService,
    private campaignService: CampaignsApiService,
    private appService: AppService,
    private usersApiService: UsersApiService,
    private formsApiService: FormsApiService,
    private tasksApi: TasksApiService,
    public activeModal: NgbActiveModal,
    private translate: TranslateService,
  ) {
    // Set default due date to 3 days from today
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 3);
    this.moveDateToNextWeekday(dueDate);
    const yyyy = dueDate.getFullYear();
    const mm = String(dueDate.getMonth() + 1).padStart(2, '0');
    const dd = String(dueDate.getDate()).padStart(2, '0');
    const dueDateStr = `${yyyy}-${mm}-${dd}`;
    this.creativeForm = this.fb.group({
      FormType: ['Creative Request'],
      Requestor: [''],
      requestType: ['', Validators.required],
      DueDate: [dueDateStr, [Validators.required, this.weekdayDateValidator]],
      AssignTo: [null, Validators.required],
      DueDate2: [dueDateStr],
      AssignTo2: [null],
      Brand: ['', Validators.required],
      campaignId: [''],
      clientId: [''],
      copyTypes: [[] as string[]],
      copyTypeOther: [''],
      creativeType: [''],
      Details: ['', Validators.required],
      EmailSent: ['', Validators.required],
      SendRequesterEmail: [false],
    });

    this.dataFeedService.getAllTaskAssignees()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.good) {
          const allowed = new Set(
            this.allowedAssigneeNames.map((n) => this.makeNameKey(n.firstName, n.lastName))
          );

          this.creativeUsers = [...response.data]
            .filter((u) => allowed.has(this.makeNameKey(u?.firstName, u?.lastName)))
            .map((u) => ({
              ...u,
              fullName: `${u?.firstName ?? ''} ${u?.lastName ?? ''}`.trim(),
            }));

          const assignToControl = this.creativeForm.get('AssignTo');
          const currentAssigneeId = assignToControl?.value;
          const isCurrentValid = this.creativeUsers.some((u) => u?.id === currentAssigneeId);
          if (!isCurrentValid) {
            assignToControl?.setValue(null, { emitEvent: false });
          }

          const assignTo2Control = this.creativeForm.get('AssignTo2');
          const currentAssignee2Id = assignTo2Control?.value;
          const isCurrent2Valid = this.creativeUsers.some((u) => u?.id === currentAssignee2Id);
          if (!isCurrent2Valid) {
            assignTo2Control?.setValue(null, { emitEvent: false });
          }
          // console.log(this.creativeUsers);
        }
      });

    this.requestTypeOptions = [
      { value: 'Copy', label: this.translate.instant('COPY') },
      { value: 'Creative', label: this.translate.instant('CREATIVE') },
      { value: this.copyCreativeValue, label: this.translate.instant('COPY_AND_CREATIVE') },
    ];

    this.projectTypeOptions = [
      { value: 'Text Overlay', label: this.translate.instant('TEXT_OVERLAY') },
      { value: 'Image Resize/Reformat', label: this.translate.instant('IMAGE_RESIZE_REFORMAT') },
      { value: 'Landing Page', label: this.translate.instant('LANDING_PAGE') },
      { value: 'Social Creative Pull', label: this.translate.instant('SOCIAL_CREATIVE_PULL') },
      { value: 'Pitch Deck Update Existing', label: this.translate.instant('PITCH_DECK_UPDATE_EXISTING') },
      { value: 'Pitch Deck NEW', label: this.translate.instant('PITCH_DECK_NEW') },
      { value: 'Asset Upload', label: this.translate.instant('ASSET_UPLOAD') },
      { value: 'Cost Sheet Update', label: this.translate.instant('COST_SHEET_UPDATE') },
      { value: "Andre's Big Ideas", label: this.translate.instant('ANDRES_BIG_IDEAS') },
      { value: 'Other (Specify In Details)', label: this.translate.instant('OTHER_SPECIFY_IN_DETAILS') },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    const nextCampaignId = changes?.['campaignId']?.currentValue;
    if (typeof nextCampaignId === 'string' && nextCampaignId.trim()) {
      // If campaignId input changes after init, keep the form in sync.
      this.loadCampaignAndApply(nextCampaignId.trim());
      return;
    }

    const nextClientId = changes?.['clientId']?.currentValue;
    if (typeof nextClientId === 'string' && nextClientId.trim()) {
      // Only apply clientId input when campaignId isn't driving the selection.
      if (!String(this.campaignId ?? '').trim()) {
        this.applyClientIdAndBrand(nextClientId.trim());
      }
    }
  }

  ngOnInit() {
    // Capture current user email for use in the form/payload
    const userId = this.appService.getActiveUser$().value?.id || '';
    this.usersApiService.getUserDetails(userId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response) {
          this.creativeForm.get('Requestor')?.setValue(response.data.firstName + ' ' + response.data.lastName);
          this.requestorEmail = String(response?.data?.email ?? '').trim();
        }
      });
      
    // Load clients regardless (used for client->brand autofill and fallback behavior).
    this.dataFeedService.getAllClients()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.good) {
          this.allClients = response.data.map((i: any) => ({ ...i, fullName: i.firstName + ' ' + i.lastName }));
          this.tryAutofillBrandFromClientId(this.creativeForm.get('clientId')?.value);
        }
      });

    // Always load campaigns so the Campaign dropdown can be filtered by client,
    // regardless of whether clientId/campaignId are set via @Input or the form.
    this.appService.getAllCampaigns$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        const data = Array.isArray(response?.data) ? response.data : [];
        this.allCampaigns = data.filter((campaign: any) => {
          return !campaign?.isDeleted && campaign?.stage !== 'Inactive';
        });
        this.filterCampaignsByClient();
      });

    this.creativeForm.get('clientId')?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((clientId) => {
        this.filterCampaignsByClient();
        const campaignIdControl = this.creativeForm.get('campaignId');
        const selectedCampaignId = this.normalizeId(campaignIdControl?.value);
        const nextClientId = this.normalizeId(clientId);
        if (selectedCampaignId && nextClientId) {
          const selectedCampaign = (this.allCampaigns ?? []).find((c: any) => this.normalizeId(c?.id) === selectedCampaignId);
          const belongsToClient = this.getCampaignClientId(selectedCampaign) === nextClientId;
          if (!belongsToClient) {
            campaignIdControl?.setValue('', { emitEvent: false });
          }
        } else if (!nextClientId) {
          campaignIdControl?.setValue('', { emitEvent: false });
        }
        this.tryAutofillBrandFromClientId(clientId);
      });

    ['DueDate', 'DueDate2'].forEach((controlName) => {
      this.creativeForm.get(controlName)?.valueChanges
        .pipe(takeUntil(this.destroyed$))
        .subscribe((value) => {
          const date = this.parseDateOnlyToLocalDate(value);
          if (!date || (date.getDay() !== 0 && date.getDay() !== 6)) {
            return;
          }

          this.moveDateToNextWeekday(date);
          this.creativeForm.get(controlName)?.setValue(this.formatDateOnly(date), { emitEvent: false });
        });
    });

    this.creativeForm.get('campaignId')?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((campaignId) => {
        const selectedCampaignId = this.normalizeId(campaignId);
        if (!selectedCampaignId) {
          return;
        }

        const campaign = (this.allCampaigns ?? []).find((c: any) => this.normalizeId(c?.id) === selectedCampaignId);
        const campaignClientId = this.getCampaignClientId(campaign);
        if (!campaignClientId) {
          return;
        }

        // Setting clientId will also trigger Brand autofill and re-filter campaigns.
        this.creativeForm.get('clientId')?.setValue(campaignClientId);
      });

    this.appService.fetchAllCampaigns({});

    // Apply initial inputs after subscriptions are set up.
    if (this.clientId) {
      this.applyClientIdAndBrand(this.clientId);
    }
    if (this.campaignId) {
      this.loadCampaignAndApply(this.campaignId);
    }

    this.dataFeedService.getAssociatedCompanies()
      .subscribe(res => {
        const uniqueCompanies = res.data.filter((company: ICompany, index: number, self: ICompany[]) =>
          index === self.findIndex((c) => c.id === company.id)
        );
        this.companiesData = [...uniqueCompanies];

        // If client was selected before companies loaded, backfill brand now.
        this.tryAutofillBrandFromClientId(this.creativeForm.get('clientId')?.value);
      });

    // Conditional validation: Project Type is required only for Creative/Copy & Creative
    // Copy Type "Other" text is required only if "Other" is checked.
    this.creativeForm.get('requestType')?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val) => {
        this.updateProjectTypeValidator(val);
        this.updateCopyTypeControls(val);
        this.updateSecondTaskControls(val);
      });

    const initialRequestType = this.creativeForm.get('requestType')?.value;
    this.updateProjectTypeValidator(initialRequestType);
    this.updateCopyTypeControls(initialRequestType);
    this.updateSecondTaskControls(initialRequestType);
  }

  private tryAutofillBrandFromClientId(clientId: unknown): void {
    const clientIdValue = String(clientId ?? '').trim();
    const brandControl = this.creativeForm.get('Brand');
    if (!brandControl) {
      return;
    }

    const currentBrand = String(brandControl.value ?? '').trim();

    // If client is cleared, clear Brand only if it was auto-filled.
    if (!clientIdValue) {
      if (this.lastAutoBrandName && currentBrand === this.lastAutoBrandName) {
        brandControl.setValue('', { emitEvent: false });
      }
      this.lastAutoBrandName = null;
      this.lastAutoBrandClientId = null;
      return;
    }

    const client = (this.allClients ?? []).find((c) => c?.id === clientIdValue);
    if (!client) {
      return;
    }

    const companyFromBrandId = client?.brandId
      ? (this.companiesData ?? []).find((c) => c?.id === client.brandId)
      : null;

    const nextBrand = String(companyFromBrandId?.name ?? client?.brand ?? '').trim();
    if (!nextBrand) {
      return;
    }

    const shouldOverwrite =
      !currentBrand ||
      (this.lastAutoBrandName !== null && currentBrand === this.lastAutoBrandName);

    if (!shouldOverwrite) {
      return;
    }

    brandControl.setValue(nextBrand, { emitEvent: false });
    this.lastAutoBrandName = nextBrand;
    this.lastAutoBrandClientId = clientIdValue;
  }

  private loadCampaignAndApply(campaignId: string): void {
    const trimmed = String(campaignId ?? '').trim();
    if (!trimmed) {
      return;
    }

    this.creativeForm.get('campaignId')?.setValue(trimmed, { emitEvent: false });
    this.campaignService.getCampaignDetails(trimmed)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        const campaign = response?.data;
        if (!campaign) {
          return;
        }

        // Ensure this campaign is present in the list if the full list hasn't loaded yet.
        // (Once all campaigns arrive, filtering will re-run.)
        if (!(this.allCampaigns ?? []).some((c: any) => this.normalizeId(c?.id) === this.normalizeId(campaign?.id))) {
          this.allCampaigns = [...(this.allCampaigns ?? []), campaign];
        }

        const campaignClientId = this.getCampaignClientId(campaign);
        if (campaignClientId) {
          // Keep client/brand in sync with the selected campaign.
          this.creativeForm.get('clientId')?.setValue(campaignClientId, { emitEvent: false });
          this.tryAutofillBrandFromClientId(campaignClientId);
          this.filterCampaignsByClient();
        }
      });
  }

  private applyClientIdAndBrand(clientId: string): void {
    const trimmed = String(clientId ?? '').trim();
    if (!trimmed) {
      return;
    }

    this.creativeForm.get('clientId')?.setValue(trimmed, { emitEvent: false });
    this.tryAutofillBrandFromClientId(trimmed);
    this.filterCampaignsByClient();
  }

  private updateProjectTypeValidator(requestType: string) {
    const creativeTypeControl = this.creativeForm.get('creativeType');
    if (!creativeTypeControl) {
      return;
    }

    const shouldRequire = requestType === 'Creative' || requestType === 'Copy & Creative';

    if (shouldRequire) {
      creativeTypeControl.setValidators([Validators.required]);
    } else {
      creativeTypeControl.clearValidators();
      creativeTypeControl.setValue('', { emitEvent: false });
    }

    creativeTypeControl.updateValueAndValidity({ emitEvent: false });
  }

  private updateCopyTypeControls(requestType: string) {
    const copyTypesControl = this.creativeForm.get('copyTypes');
    const otherControl = this.creativeForm.get('copyTypeOther');
    if (!copyTypesControl || !otherControl) {
      return;
    }

    const shouldShowCopyTypes = requestType === 'Copy' || requestType === 'Copy & Creative';
    if (!shouldShowCopyTypes) {
      copyTypesControl.setValue([], { emitEvent: false });
      otherControl.clearValidators();
      otherControl.setValue('', { emitEvent: false });
      otherControl.updateValueAndValidity({ emitEvent: false });
      return;
    }

    this.syncCopyTypeOtherValidator();
  }

  private updateSecondTaskControls(requestType: string) {
    const dueDate2Control = this.creativeForm.get('DueDate2');
    const assignTo2Control = this.creativeForm.get('AssignTo2');
    if (!dueDate2Control || !assignTo2Control) {
      return;
    }

    const shouldRequireSecond = requestType === this.copyCreativeValue;
    if (shouldRequireSecond) {
      dueDate2Control.setValidators([Validators.required, this.weekdayDateValidator]);
      assignTo2Control.setValidators([Validators.required]);

      const current = String(dueDate2Control.value ?? '').trim();
      if (!current) {
        const firstDueDate = String(this.creativeForm.get('DueDate')?.value ?? '').trim();
        if (firstDueDate) {
          dueDate2Control.setValue(firstDueDate, { emitEvent: false });
        }
      }
    } else {
      dueDate2Control.clearValidators();
      assignTo2Control.clearValidators();
      dueDate2Control.setValue('', { emitEvent: false });
      assignTo2Control.setValue(null, { emitEvent: false });
    }

    dueDate2Control.updateValueAndValidity({ emitEvent: false });
    assignTo2Control.updateValueAndValidity({ emitEvent: false });
  }

  public onCopyTypeToggle(value: string, checked: boolean) {
    const control = this.creativeForm.get('copyTypes');
    if (!control) {
      return;
    }

    const current = Array.isArray(control.value) ? [...control.value] : [];
    const next = checked
      ? Array.from(new Set([...current, value]))
      : current.filter((v) => v !== value);

    control.setValue(next);
    this.syncCopyTypeOtherValidator();
  }

  public isCopyTypeSelected(copyTypeValue: string): boolean {
    const control = this.creativeForm.get('copyTypes');
    const rawValue = control?.value;
    const current: string[] = Array.isArray(rawValue) ? rawValue : [];
    return current.includes(copyTypeValue);
  }

  private syncCopyTypeOtherValidator() {
    const otherControl = this.creativeForm.get('copyTypeOther');
    if (!otherControl) {
      return;
    }

    const shouldRequireOther = this.isCopyTypeSelected(this.copyTypeOtherValue);
    if (shouldRequireOther) {
      otherControl.setValidators([Validators.required]);
    } else {
      otherControl.clearValidators();
      otherControl.setValue('', { emitEvent: false });
    }

    otherControl.updateValueAndValidity({ emitEvent: false });
  }

  filterCampaignsByClient() {
    const clientId = this.normalizeId(this.creativeForm.get('clientId')?.value);
    if (clientId) {
      this.campaignsData = (this.allCampaigns ?? []).filter((c: any) => this.getCampaignClientId(c) === clientId);
      return;
    }

    this.campaignsData = [...(this.allCampaigns ?? [])];
  }

  private parseDateOnlyToLocalDate(value: unknown): Date | null {
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    const raw = String(value ?? '').trim();
    // HTML date input produces YYYY-MM-DD
    const match = raw.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return null;
    }

    const local = new Date(year, month - 1, day);
    return isNaN(local.getTime()) ? null : local;
  }

  private moveDateToNextWeekday(date: Date): void {
    if (date.getDay() === 6) {
      date.setDate(date.getDate() + 2);
    } else if (date.getDay() === 0) {
      date.setDate(date.getDate() + 1);
    }
  }

  private formatDateOnly(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private buildFormattedBody(values: any, target: { campaignId?: string | null; clientId: string | null; campaignName?: string; clientName?: string }): string {
    const safe = (v: any) => {
      const s = String(v ?? '').trim();
      return s.length ? s : '—';
    };

    const copyTypes: string[] = Array.isArray(values?.copyTypes) ? values.copyTypes : [];
    const copyTypesText = copyTypes.length ? copyTypes.join(', ') : '—';
    const hasOther = copyTypes.includes(this.copyTypeOtherValue);
    const otherText = hasOther ? safe(values?.copyTypeOther) : '—';

    const assignee = (this.creativeUsers ?? []).find((u: any) => u?.id === values?.AssignTo);
    const assigneeName = assignee?.fullName ?? '';
    const assigneeDisplay = assigneeName
      ? `${assigneeName}`
      : safe(values?.AssignTo);

    const lines: string[] = [];

    lines.push(`${this.translate.instant('REQUESTED_BY')}: ${safe(values?.Requestor)}`);
    // lines.push(`Type of Request: ${safe(values?.requestType)}`);
    // lines.push('');
    if (values?.requestType === this.copyCreativeValue) {
      lines.push(`${this.translate.instant('COPY_NEEDED_BY')}: ${safe(values?.DueDate)}`);
      // lines.push(`Copy Assigned To: ${assigneeDisplay}`);
      // lines.push('');
      lines.push(`${this.translate.instant('CREATIVE_NEEDED_BY')}: ${safe(values?.DueDate2)}`);

      const assignee2 = (this.creativeUsers ?? []).find((u: any) => u?.id === values?.AssignTo2);
      const assignee2Name = assignee2?.fullName ?? '';
      const assignee2Display = assignee2Name
        ? `${assignee2Name}`
        : safe(values?.AssignTo2);
      // lines.push(`Creative Assigned To: ${assignee2Display}`);
    } else {
      lines.push(`${this.translate.instant('NEEDED_BY')}: ${safe(values?.DueDate)}`);
      // lines.push(`Assigned To: ${assigneeDisplay}`);
    }

    lines.push('');
    lines.push(`${this.translate.instant('BRAND')}: ${safe(values?.Brand)}`);

    if (target?.campaignId) {
      lines.push(`${this.translate.instant('CAMPAIGN')}: ${safe(target.campaignName)}`);
    } 

    if (values?.requestType === 'Creative' || values?.requestType === 'Copy & Creative') {
      lines.push('');
      lines.push(`${this.translate.instant('CREATIVE_PROJECT_TYPE')}: ${safe(values?.creativeType)}`);
    }

    if (values?.requestType === 'Copy' || values?.requestType === 'Copy & Creative') {
      lines.push('');
      lines.push(`${this.translate.instant('COPY_PROJECT_TYPES')}: ${copyTypesText}`);
      if (hasOther) {
        lines.push(`${this.translate.instant('OTHER')}: ${otherText}`);
      }
    }

    lines.push('');
    lines.push(this.translate.instant('REQUEST_DETAILS'));
    lines.push(safe(values?.Details));
    lines.push('');
    lines.push(`${this.translate.instant('EMAIL_SENT_WITH_ASSETS')}: ${safe(values?.EmailSent)}`);

    return lines.join('\n');
  }

  private escapeHtml(value: unknown): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private buildHtmlBodyFromPlain(plainText: unknown): string {
    const escaped = this.escapeHtml(plainText);
    // Preserve newlines while staying safe against injection.
    return `<div style="white-space:pre-wrap">${escaped}</div>`;
  }

  private resolveTarget(values: any): { campaignId?: string | null; clientId: string | null; campaignName?: string; clientName?: string } {
    const campaignId = String(values?.campaignId ?? '').trim();
    const clientId = String(values?.clientId ?? '').trim();

    if (campaignId) {
      const campaign = [...(this.allCampaigns ?? []), ...(this.campaignsData ?? [])].find((c: any) => c?.id === campaignId);
      const resolvedClientId = String(campaign?.clientId ?? clientId ?? '').trim();
      return {
        campaignId,
        campaignName: campaign?.name,
        clientId: resolvedClientId || null,
      };
    }

    if (clientId) {
      const client = (this.allClients ?? []).find((c: any) => c?.id === clientId);
      return { clientId, clientName: client?.fullName };
    }

    return { clientId: null };
  }

  private resolveAssigneeEmail(assignedUserId: unknown) {
    const id = String(assignedUserId ?? '').trim();
    if (!id) return of('');

    const fromList = (this.creativeUsers ?? []).find((u: any) => String(u?.id ?? '').trim() === id);
    const listEmail = String((fromList as any)?.email ?? '').trim();
    if (listEmail) return of(listEmail);

    // Fallback: assignee list may not include email in some environments.
    return this.usersApiService.getUserDetails(id).pipe(
      map((resp: any) => String(resp?.data?.email ?? '').trim()),
      catchError(() => of(''))
    );
  }

  onSubmit() {
    if (this.creativeForm.invalid) {
      this.creativeForm.markAllAsTouched();
      return;
    }

    const values = this.creativeForm.getRawValue();
    const target = this.resolveTarget(values);
    const body = this.buildFormattedBody(values, target);

    const activeUserId = this.appService.getActiveUser$().value?.id ?? '';
    const assignedUser = (this.creativeUsers ?? []).find((u: any) => u?.id === values?.AssignTo);
    const assignedUserName = assignedUser?.fullName ?? '';

    const assignedUser2 = (this.creativeUsers ?? []).find((u: any) => u?.id === values?.AssignTo2);
    const assignedUser2Name = assignedUser2?.fullName ?? '';

    const now = new Date();
    const titleSuffix = String(values?.Brand ?? '').trim();
    const requestTypeLabel =
      values?.requestType === this.copyCreativeValue
        ? this.translate.instant('COPY_AND_CREATIVE')
        : (values?.requestType === 'Copy' ? this.translate.instant('COPY') : this.translate.instant('CREATIVE'));
    const title = titleSuffix ? `${requestTypeLabel} ${this.translate.instant('REQUEST')} - ${titleSuffix}` : `${requestTypeLabel} ${this.translate.instant('REQUEST')}`;

    const note: INotes = {
      id: createGuid(),
      updatedOn: now,
      assignedUserId: activeUserId,
      assignedUserName: '',
      title,
      description: body,
      type: 1,
      clientId: target?.campaignId ? '' : (target.clientId ?? ''),
      campaignId: (target?.campaignId ?? null) as any,
      campaignName: target?.campaignName ?? '',
      isInternal: true,
      noteFiles: [],
    };

    const buildTask = (taskTitle: string, dueDateValue: unknown, assignedToId: unknown, assignedToName: string): ITask => {
      const dueDateLocal = this.parseDateOnlyToLocalDate(dueDateValue) ?? new Date(String(dueDateValue ?? ''));
      const dueDateNormalized = isNaN(dueDateLocal.getTime()) ? dueDateValue : Utility.getTaskDueDateFromDateTime(dueDateLocal);

      return {
        id: createGuid(),
        title: taskTitle,
        priority: 2,
        description: body,
        clientId: (target?.clientId ?? null) as any,
        campaignId: (target?.campaignId ?? null) as any,
        dueDate: dueDateNormalized as any,
        completedOn: null,
        isCompleted: false,
        assignedUserId: assignedToId as any,
        assignedUserName: assignedToName,
      };
    };

    const isCopyAndCreative = values?.requestType === this.copyCreativeValue;
    const tasksToSave: ITask[] = [];

    let copyTaskTitle = title;
    let creativeTaskTitle = title;

    if (isCopyAndCreative) {
      copyTaskTitle = titleSuffix ? `${this.translate.instant('COPY_REQUEST_COPY_AND_CREATIVE')} - ${titleSuffix}` : this.translate.instant('COPY_REQUEST_COPY_AND_CREATIVE');
      creativeTaskTitle = titleSuffix ? `${this.translate.instant('CREATIVE_REQUEST_COPY_AND_CREATIVE')} - ${titleSuffix}` : this.translate.instant('CREATIVE_REQUEST_COPY_AND_CREATIVE');
      tasksToSave.push(buildTask(copyTaskTitle, values?.DueDate, values?.AssignTo, assignedUserName));
      tasksToSave.push(buildTask(creativeTaskTitle, values?.DueDate2, values?.AssignTo2, assignedUser2Name));
    } else {
      tasksToSave.push(buildTask(title, values?.DueDate, values?.AssignTo, assignedUserName));
    }

    const buildEmail = (toEmail: string, subject: string, ccs?: string): EmailRequestModel => ({
      ToEmail: toEmail,
      Subject: subject,
      ReplyToEmail: this.requestorEmail,
      PlainContent: body,
      HtmlContent: this.buildHtmlBodyFromPlain(body),
      CcEmails: ccs ?? '',
      BccEmails: '',
      FromName: this.translate.instant('CREATIVE_REQUEST_FORM'),
    });

    // Send email notification(s).
    const primarySubject = isCopyAndCreative
      ? `${this.translate.instant('NEW_COPY_REQUEST_ASSIGNED')}: ${copyTaskTitle} (${this.translate.instant('COPY_AND_CREATIVE')})`
      : `${this.translate.instant('NEW_REQUEST_ASSIGNED')}: ${title}`;

    const buildEmailRequestForAssignee = (assignedUserId: unknown, subject: string, ccs?: string) => {
      return this.resolveAssigneeEmail(assignedUserId).pipe(
        map((toEmail: string) => {
          if (!toEmail) {
            console.warn('No assignee email found; notification will not be sent.', { assignedUserId });
            return null;
          }

          return buildEmail(toEmail, subject, ccs);
        }),
        catchError((emailLookupError) => {
          console.error('Error resolving assignee email:', emailLookupError);
          return of(null);
        })
      );
    };

    const sendNotificationEmails = () => {
      const requesterCc = values?.SendRequesterEmail ? this.requestorEmail : '';
      const emailRequestStreams = [
        buildEmailRequestForAssignee(values?.AssignTo, primarySubject, requesterCc),
      ];

      if (isCopyAndCreative) {
        const subject2 = `${this.translate.instant('NEW_CREATIVE_REQUEST_ASSIGNED')}: ${creativeTaskTitle} (${this.translate.instant('COPY_AND_CREATIVE')})`;
        emailRequestStreams.push(buildEmailRequestForAssignee(values?.AssignTo2, subject2));
      }

      return forkJoin(emailRequestStreams).pipe(
        switchMap((emailRequests: Array<EmailRequestModel | null>) => {
          const sendEmailStreams = emailRequests
            .filter((request): request is EmailRequestModel => !!request)
            .map((request) => this.formsApiService.sendEmail(request).pipe(
              catchError((emailError) => {
                console.error('Error sending email:', emailError);
                return of(null);
              })
            ));

          return sendEmailStreams.length ? forkJoin(sendEmailStreams) : of([]);
        })
      );
    };

    this.creativeForm.disable();

    forkJoin([
      this.dataFeedService.saveNote(note),
      ...tasksToSave.map((t) => this.tasksApi.saveTask(t)),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          sendNotificationEmails()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
              next: () => this.activeModal.close(response),
              error: (emailError) => {
                console.error('Error sending notification emails:', emailError);
                this.activeModal.close(response);
              }
            });
        },
        error: (error) => {
          console.error('Error creating note/task:', error);
          this.creativeForm.enable();
        }
      });
  }

  close() {
    this.activeModal.dismiss();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
